import React, { useState, useEffect } from 'react';
import { Layout, Card, Table, Button, DatePicker, Select, message, Space, Typography, Tag, Menu, Input, Modal, Form, Radio, TimePicker, Input as AntInput, Image, Row, Col, Popconfirm, Checkbox, Spin, Badge, Tabs } from 'antd';
import './AttendanceManagement.css';
import {
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ExportOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  FilterOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  SearchOutlined,
  DeleteOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const parseTimeValue = (value) => {
  if (!value) return null;
  const v = String(value).trim();
  if (!v || v === '-' || v.toLowerCase() === 'invalid date') return null;

  let d = dayjs(v, 'HH:mm:ss', true);
  if (d.isValid()) return d;
  d = dayjs(v, 'HH:mm', true);
  if (d.isValid()) return d;
  d = dayjs(v);
  if (d.isValid()) return d;
  return null;
};

const formatWorkingHours = (checkInRaw, checkOutRaw, totalWorkHours) => {
  const hours = Number(totalWorkHours);
  if (Number.isFinite(hours) && hours > 0) {
    return `${hours.toFixed(2)}h`;
  }
  const checkIn = parseTimeValue(checkInRaw);
  const checkOut = parseTimeValue(checkOutRaw);
  if (!checkIn || !checkOut) return '-';
  let minutes = checkOut.diff(checkIn, 'minute');
  if (minutes < 0) minutes += 1440; // Add 24 hours for night shifts
  if (!Number.isFinite(minutes)) return '-';
  return `${(minutes / 60).toFixed(2)}h`;
};

const AttendanceManagement = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [staffNameFilter, setStaffNameFilter] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [markOpen, setMarkOpen] = useState(false);
  const [bulkMarkOpen, setBulkMarkOpen] = useState(false);
  const [markForm] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [bulkStaffIds, setBulkStaffIds] = useState([]); // selected staff ids
  const [bulkRows, setBulkRows] = useState([]);   // per-date-staff rows: [{key, userId, staffName, date, dateStr, formattedDate, status, checkInDate, checkIn, checkOutDate, checkOut}]
  const [bulkDateMode, setBulkDateMode] = useState('single'); // 'single' | 'range' | 'multiple'
  const [bulkDate, setBulkDate] = useState(dayjs());
  const [bulkDateRange, setBulkDateRange] = useState([dayjs(), dayjs()]);
  const [bulkMultipleDates, setBulkMultipleDates] = useState([dayjs()]);
  const [masterStatus, setMasterStatus] = useState('present');
  const [masterCheckIn, setMasterCheckIn] = useState(parseTimeValue('09:30'));
  const [masterCheckOut, setMasterCheckOut] = useState(parseTimeValue('18:00'));
  const [excludeWeeklyOff, setExcludeWeeklyOff] = useState(false);
  const [excludeHoliday, setExcludeHoliday] = useState(false);
  const [excludePaidLeave, setExcludePaidLeave] = useState(false);
  const [effectiveTemplate, setEffectiveTemplate] = useState(null);
  const [effectiveShift, setEffectiveShift] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteForm] = Form.useForm();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsData, setLogsData] = useState(null);
  const [staffOnLeave, setStaffOnLeave] = useState(false);
  const [staffOffMsg, setStaffOffMsg] = useState(null);
  const [selectedStaffDeactivated, setSelectedStaffDeactivated] = useState(false);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [rmoStaffIds, setRmoStaffIds] = useState([]);
  const [isRmoSelected, setIsRmoSelected] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const [cardModalVisible, setCardModalVisible] = useState(false);
  const [cardModalType, setCardModalType] = useState('');
  const [cardModalTitle, setCardModalTitle] = useState('');
  const [cardModalData, setCardModalData] = useState([]);
  const [cardModalLoading, setCardModalLoading] = useState(false);
  const [cardSearchText, setCardSearchText] = useState('');


  const [regModalOpen, setRegModalOpen] = useState(false);
  const [regList, setRegList] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regStatusFilter, setRegStatusFilter] = useState('ALL');
  const [pendingRegCount, setPendingRegCount] = useState(0);
  const [reviewingId, setReviewingId] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingRecord, setRejectingRecord] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');

  const [lateList, setLateList] = useState([]);
  const [lateLoading, setLateLoading] = useState(false);
  const [lateStatusFilter, setLateStatusFilter] = useState('ALL');
  const [pendingLateCount, setPendingLateCount] = useState(0);
  const [reviewingLateId, setReviewingLateId] = useState(null);
  const [regModalActiveTab, setRegModalActiveTab] = useState('regularization');

  const fetchLateApprovals = async () => {
    setLateLoading(true);
    try {
      const resp = await api.get('/admin/late-approval/list');
      if (resp.data?.success) {
        const list = resp.data.list || [];
        setLateList(list);
        setPendingLateCount(list.filter(r => r.status === 'PENDING').length);
      }
    } catch (err) {
      console.error('Failed to fetch late approvals:', err);
    } finally {
      setLateLoading(false);
    }
  };

  const handleReviewLateApproval = async (id, status, adminRemarks = null) => {
    setReviewingLateId(id);
    try {
      const res = await api.patch(`/admin/late-approval/${id}/review`, { status, adminRemarks });
      if (res.data?.success) {
        message.success(`Late check-in ${status.toLowerCase()} successfully`);
        await fetchLateApprovals();
        fetchAttendance();
      } else {
        message.error(res.data?.message || 'Action failed');
      }
    } catch (err) {
      console.error('Late review error:', err);
      message.error(err.response?.data?.message || 'Failed to update late approval');
    } finally {
      setReviewingLateId(null);
    }
  };

  const fetchRegularizations = async () => {
    setRegLoading(true);
    try {
      const resp = await api.get('/attendance/regularization/list');
      if (resp.data?.success) {
        const list = resp.data.regularizations || [];
        setRegList(list);
        setPendingRegCount(list.filter(r => r.status === 'PENDING').length);
      }
    } catch (err) {
      console.error('Failed to fetch regularizations:', err);
    } finally {
      setRegLoading(false);
    }
  };

  const handleReviewRegularization = async (id, status, adminRemarks = null) => {
    setReviewingId(id);
    try {
      const res = await api.patch(`/attendance/regularization/${id}`, { status, adminRemarks });
      if (res.data?.success) {
        message.success(`Regularization request ${status.toLowerCase()} successfully`);
        await fetchRegularizations();
        fetchAttendance();
      } else {
        message.error(res.data?.message || 'Action failed');
      }
    } catch (err) {
      console.error('Review error:', err);
      message.error(err.response?.data?.message || 'Failed to update regularization');
    } finally {
      setReviewingId(null);
      setRejectModalOpen(false);
      setRejectingRecord(null);
      setRejectRemarks('');
    }
  };

  const handleCardClick = async (type, title) => {
    setCardModalType(type);
    setCardModalTitle(title);
    setCardModalVisible(true);
    setCardModalLoading(true);
    setCardSearchText('');
    try {
      const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined;
      const resp = await api.get('/admin/dashboard/card-details', { params: { type, date: dateStr } });
      if (resp.data.success) {
        setCardModalData(resp.data.data || []);
      } else {
        setCardModalData([]);
      }
    } catch (err) {
      console.error('Failed to fetch card details:', err);
      setCardModalData([]);
    } finally {
      setCardModalLoading(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    fetchStaff();
    fetchDepartments();
    fetchAttendance();
    fetchApprovedLeaves();
    fetchRegularizations();
    fetchLateApprovals();

    const fetchRmoSettings = async () => {
      try {
        const resp = await api.get('/admin/rmo-settings');
        const settings = resp?.data?.settings || { targetHours: 480, staffIds: [] };
        setRmoStaffIds(settings.staffIds || []);
      } catch (e) {
        console.error('Failed to load RMO settings:', e);
      }
    };
    fetchRmoSettings();

    const fetchSubscriptionInfo = async () => {
      try {
        const resp = await api.get('/subscription/subscription-info');
        setSubscriptionInfo(resp.data?.subscriptionInfo || null);
      } catch (e) {
        console.error('Failed to load subscription info:', e);
      }
    };
    fetchSubscriptionInfo();
  }, []);

  useEffect(() => {
    fetchStaff();
    fetchAttendance();
    fetchApprovedLeaves();
    fetchRegularizations();
    fetchLateApprovals();
  }, [selectedDate, selectedStaff]);

  // Load effective template when a specific staff is selected
  useEffect(() => {
    const loadTplAndShift = async () => {
      if (!selectedStaff || selectedStaff === 'all') {
        setEffectiveTemplate(null);
        setEffectiveShift(null);
        return;
      }
      try {
        const [tplRes, shiftRes] = await Promise.all([
          api.get(`/admin/settings/attendance-templates/effective/${selectedStaff}`),
          api.get(`/admin/shifts/effective/${selectedStaff}`)
        ]);
        setEffectiveTemplate(tplRes.data?.template || null);
        setEffectiveShift(shiftRes.data?.shift || null);
      } catch (_) {
        setEffectiveTemplate(null);
        setEffectiveShift(null);
      }
    };
    loadTplAndShift();
  }, [selectedStaff]);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/admin/staff?module=attendance');
      if (response.data.success) {
        const arr = Array.isArray(response.data.staff) ? response.data.staff : (Array.isArray(response.data.data) ? response.data.data : []);
        setStaffList(arr);
      } else {
        setStaffList([]);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      setStaffList([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const resp = await api.get('/admin/business-functions');
      const list = resp?.data?.data || [];
      const deptFn = list.find((f) => String(f.name || '').toLowerCase() === 'department');
      const values = Array.isArray(deptFn?.values) ? deptFn.values : [];
      const items = values
        .filter((v) => v && v.value)
        .map((v) => ({ id: v.id, name: v.value }));
      setDepartments(items);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      setDepartments([]);
    }
  };

  const fetchSpecialDayStatus = (uid, dateStr) => {
    if (!uid || !dateStr) return;
    api.get(`/admin/attendance/check-special-day?userId=${uid}&date=${dateStr}`)
      .then(res => {
        if (res.data?.success && res.data.isSpecial) {
          setStaffOffMsg(res.data.message);
        } else {
          setStaffOffMsg(null);
        }
      })
      .catch(() => setStaffOffMsg(null));
  };

  const openMarkModal = (staffRow = null) => {
    markForm.resetFields();

    const staffId = staffRow?.userId ?? staffRow?.id ?? (selectedStaff !== 'all' ? selectedStaff : undefined);

    const existingRecord = staffId
      ? attendance.find(a => (a.userId === staffId || a.userId === Number(staffId)) && a.date === selectedDate.format('YYYY-MM-DD'))
      : null;

    const parsedIn = parseTimeValue(existingRecord?.checkIn) || parseTimeValue('09:30');
    const parsedOut = parseTimeValue(existingRecord?.checkOut) || parseTimeValue('18:00');
    const isNight = parsedIn && parsedOut && parsedOut.isBefore(parsedIn);

    markForm.setFieldsValue({
      staffId,
      date: selectedDate,
      checkInDate: existingRecord?.punchedInAt ? dayjs(existingRecord.punchedInAt) : selectedDate,
      checkOutDate: existingRecord?.punchedOutAt ? dayjs(existingRecord.punchedOutAt) : (isNight ? selectedDate.add(1, 'day') : selectedDate),
      status: existingRecord?.status || 'present',
      checkIn: parsedIn,
      checkOut: parsedOut,
      overtimeMinutes: null,
    });

    if (staffId && !existingRecord) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      api.get(`/admin/shifts/effective/${staffId}?date=${dateStr}`).then(res => {
        const shift = res.data?.shift;
        if (shift) {
          markForm.setFieldsValue({
            checkIn: parseTimeValue(shift.startTime) || parseTimeValue('09:30'),
            checkOut: parseTimeValue(shift.endTime) || parseTimeValue('18:00'),
          });
        }
      }).catch(() => { });
    }
    const staffInfo = staffList.find(s => s.id === staffId || s.id === Number(staffId));
    if (staffInfo && staffInfo.active === false) {
      message.warning(`Warning: ${staffInfo.name} is currently deactivated. Attendance marked for deactivated staff may be restricted.`);
    }

    const isRmo = rmoStaffIds.map(Number).includes(Number(staffId));
    setIsRmoSelected(isRmo);

    setMarkOpen(true);
    setStaffOnLeave(false);
    setStaffOffMsg(null);
    setSelectedStaffDeactivated(false);
  };

  const submitMark = async () => {
    try {
      const values = await markForm.validateFields();

      // Block save if selected staff is RMO
      if (rmoStaffIds.map(Number).includes(Number(values.staffId))) {
        message.warning('This staff is RMO. Intermediate days are automatically marked as Present. You do not need to mark manually.');
        return;
      }
      if (values.date && values.date.isSame(dayjs(), 'day') && values.checkIn && values.checkOut) {
        const isNightShift = values.checkOut.isBefore(values.checkIn);
        
        if (!isNightShift) {
          const now = dayjs();
          const checkOutTime = now.hour(values.checkOut.hour()).minute(values.checkOut.minute()).second(values.checkOut.second());
          
          if (checkOutTime.isAfter(now)) {
            const nowStr = now.format('hh:mm A');
            const checkOutStr = values.checkOut.format('hh:mm A');
            message.error(`Current time is ${nowStr}. You can set check-out time as ${checkOutStr} only after it is ${checkOutStr}.`);
            return;
          }
        }
      }

      const payload = {
        staffId: values.staffId,
        date: values.date?.format('YYYY-MM-DD'),
        checkInDate: values.checkInDate ? values.checkInDate.format('YYYY-MM-DD') : values.date?.format('YYYY-MM-DD'),
        checkOutDate: values.checkOutDate ? values.checkOutDate.format('YYYY-MM-DD') : undefined,
        status: values.status,
        checkIn: values.checkIn ? values.checkIn.format('HH:mm:ss') : null,
        checkOut: values.checkOut ? values.checkOut.format('HH:mm:ss') : null,
        overtimeMinutes: values.status === 'overtime' && Number.isFinite(Number(values.overtimeMinutes)) ? Number(values.overtimeMinutes) : undefined,
      };
      await api.post('/admin/attendance', payload);
      message.success('Attendance saved');
      setMarkOpen(false);
      fetchAttendance();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to save attendance');
    }
  };

  const openBulkMarkModal = () => {
    setBulkStaffIds([]);
    setBulkRows([]);
    setBulkDateMode('single');
    const initDate = selectedDate || dayjs();
    setBulkDate(initDate);
    setBulkDateRange([initDate, initDate]);
    setBulkMultipleDates([initDate]);
    setBulkMarkOpen(true);
  };

  const getBulkSelectedDates = (overrideMode, overrideDate, overrideRange, overrideMulti) => {
    const mode = overrideMode || bulkDateMode;
    if (mode === 'single') {
      const d = overrideDate || bulkDate;
      return d ? [d] : [];
    }
    if (mode === 'range') {
      const range = overrideRange || bulkDateRange;
      if (!range || !range[0] || !range[1]) return [];
      const dates = [];
      let curr = range[0].startOf('day');
      const end = range[1].startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        dates.push(curr.clone());
        curr = curr.add(1, 'day');
      }
      return dates;
    }
    if (mode === 'multiple') {
      const multi = overrideMulti || bulkMultipleDates;
      return (multi || []).slice().sort((a, b) => a.valueOf() - b.valueOf());
    }
    return [];
  };

  const syncBulkRows = async (staffIds, dates, overrideExclusions = {}) => {
    const sIds = staffIds !== undefined ? staffIds : bulkStaffIds;
    const targetDates = dates !== undefined ? dates : getBulkSelectedDates();
    const exWo = overrideExclusions.excludeWeeklyOff !== undefined ? overrideExclusions.excludeWeeklyOff : excludeWeeklyOff;
    const exHol = overrideExclusions.excludeHoliday !== undefined ? overrideExclusions.excludeHoliday : excludeHoliday;
    const exLeave = overrideExclusions.excludePaidLeave !== undefined ? overrideExclusions.excludePaidLeave : excludePaidLeave;

    if (!sIds || sIds.length === 0 || !targetDates || targetDates.length === 0) {
      setBulkRows([]);
      return;
    }

    let specialDaysMap = {};
    if (exWo || exHol || exLeave) {
      try {
        const dateStrs = targetDates.map(d => d.format('YYYY-MM-DD'));
        const resp = await api.post('/admin/attendance/check-special-days-batch', {
          userIds: sIds,
          dates: dateStrs
        });
        if (resp.data?.success) {
          specialDaysMap = resp.data.results || {};
        }
      } catch (err) {
        console.error('Failed to fetch batch special days:', err);
      }
    }

    setBulkRows(prevRows => {
      const prevMap = new Map(prevRows.map(r => [r.key, r]));
      const newRows = [];

      for (const uid of sIds) {
        const staffInfo = staffList.find(s => s.id === uid || s.id === Number(uid));
        const sName = staffInfo ? `${staffInfo.name} (${staffInfo.staffId || 'N/A'})` : `Staff ${uid}`;

        for (const d of targetDates) {
          const dateStr = d.format('YYYY-MM-DD');
          const key = `${uid}_${dateStr}`;
          const specialInfo = specialDaysMap[key] || {};

          // Skip row if any checked exclusion applies to this date & staff
          if (exWo && specialInfo.isWeeklyOff) continue;
          if (exHol && specialInfo.isHoliday) continue;
          if (exLeave && specialInfo.isLeave) continue;

          if (prevMap.has(key)) {
            newRows.push(prevMap.get(key));
          } else {
            const rec = attendance.find(
              a => (a.userId === uid || a.userId === Number(uid)) && a.date === dateStr
            );
            const parsedIn = parseTimeValue(rec?.checkIn) || parseTimeValue('09:30');
            const parsedOut = parseTimeValue(rec?.checkOut) || parseTimeValue('18:00');
            const isNight = parsedIn && parsedOut && parsedOut.isBefore(parsedIn);

            const inDate = rec?.punchedInAt ? dayjs(rec.punchedInAt) : d;
            const outDate = rec?.punchedOutAt ? dayjs(rec.punchedOutAt) : (isNight ? d.add(1, 'day') : d);

            newRows.push({
              key,
              userId: uid,
              staffName: sName,
              date: d,
              dateStr,
              formattedDate: d.format('DD MMM YYYY'),
              status: rec?.status || 'present',
              checkInDate: inDate,
              checkIn: parsedIn,
              checkOutDate: outDate,
              checkOut: parsedOut,
              hasAutoOT: false,
            });
          }
        }
      }
      return newRows;
    });
  };

  const handleBulkStaffSelect = (uid) => {
    const newStaff = [...bulkStaffIds, uid];
    setBulkStaffIds(newStaff);
    syncBulkRows(newStaff, getBulkSelectedDates());

    const staffInfo = staffList.find(s => s.id === uid || s.id === Number(uid));
    if (staffInfo && staffInfo.active === false) {
      message.warning(`Warning: ${staffInfo.name} is currently deactivated.`);
    }
  };

  const handleBulkStaffDeselect = (uid) => {
    const newStaff = bulkStaffIds.filter(id => id !== uid && id !== Number(uid));
    setBulkStaffIds(newStaff);
    syncBulkRows(newStaff, getBulkSelectedDates());
  };

  const updateBulkRowByKey = (key, field, value) => {
    setBulkRows(prev => prev.map(r =>
      r.key === key ? { ...r, [field]: value } : r
    ));
  };

  const removeBulkRowByKey = (key) => {
    setBulkRows(prev => prev.filter(r => r.key !== key));
  };

  const applyMasterToAll = () => {
    if (bulkRows.length === 0) return;
    setBulkRows(prev => prev.map(r => {
      const isNight = masterCheckIn && masterCheckOut && masterCheckOut.isBefore(masterCheckIn);
      return {
        ...r,
        status: masterStatus || r.status,
        checkIn: masterCheckIn || r.checkIn,
        checkOut: masterCheckOut || r.checkOut,
        checkInDate: r.date,
        checkOutDate: isNight ? r.date.add(1, 'day') : r.date,
      };
    }));
    message.success(`Applied master timings to all ${bulkRows.length} rows`);
  };

  const submitBulkMark = async () => {
    if (bulkRows.length === 0) {
      message.warning('Please select at least one staff and date');
      return;
    }

    try {
      const now = dayjs();

      for (const row of bulkRows) {
        if (row.date.isSame(now, 'day')) {
          if (row.checkIn && row.checkOut) {
            const isNightShift = row.checkOut.isBefore(row.checkIn);
            if (!isNightShift) {
              const checkOutTime = now.hour(row.checkOut.hour()).minute(row.checkOut.minute()).second(row.checkOut.second());
              if (checkOutTime.isAfter(now)) {
                const nowStr = now.format('hh:mm A');
                const checkOutStr = row.checkOut.format('hh:mm A');
                message.error(`For ${row.staffName} (${row.formattedDate}): Current time is ${nowStr}. You can set check-out time as ${checkOutStr} only after it is ${checkOutStr}.`);
                return;
              }
            }
          }
        }
      }

      await Promise.all(bulkRows.map(row =>
        api.post('/admin/attendance', {
          staffId: row.userId,
          date: row.dateStr,
          checkInDate: row.checkInDate ? row.checkInDate.format('YYYY-MM-DD') : row.dateStr,
          checkOutDate: row.checkOutDate ? row.checkOutDate.format('YYYY-MM-DD') : undefined,
          status: row.status,
          checkIn: row.checkIn ? row.checkIn.format('HH:mm:ss') : null,
          checkOut: row.checkOut ? row.checkOut.format('HH:mm:ss') : null,
          overtimeMinutes: row.status === 'overtime' && Number.isFinite(Number(row.overtimeMinutes)) ? Number(row.overtimeMinutes) : undefined,
        })
      ));
      message.success(`Bulk attendance saved for ${bulkRows.length} record(s)!`);
      setBulkMarkOpen(false);
      setBulkStaffIds([]);
      setBulkRows([]);
      fetchAttendance();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to save bulk attendance');
    }
  };

  const deleteAttendance = async (id) => {
    try {
      await api.delete(`/admin/attendance/${id}`);
      message.success('Attendance record deleted successfully');
      fetchAttendance();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to delete attendance record');
    }
  };

  const fetchAttendance = async () => {
    if (!selectedDate) {
      setAttendance([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        date: selectedDate.format('YYYY-MM-DD'),
      };

      if (selectedStaff !== 'all') {
        params.staffId = selectedStaff;
      }

      const response = await api.get('/admin/attendance', { params });
      if (response.data.success) {
        setAttendance(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setAttendance([]);
      }
    } catch (error) {
      message.error('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedLeaves = async () => {
    if (!selectedDate) { setApprovedLeaves([]); return; }
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const res = await api.get('/leave', { params: { status: 'APPROVED' } });
      const all = Array.isArray(res.data?.leaves) ? res.data.leaves : [];
      const forDate = all.filter(l => l.startDate <= dateStr && l.endDate >= dateStr);
      setApprovedLeaves(forDate);
    } catch (_) {
      setApprovedLeaves([]);
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        date: selectedDate.format('YYYY-MM-DD'),
      };

      if (selectedStaff !== 'all') {
        params.staffId = selectedStaff;
      }

      const response = await api.get('/admin/attendance/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance-${selectedDate.format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Failed to export attendance');
    }
  };

  const openNoteModal = (record) => {
    setSelectedRecord(record);
    noteForm.resetFields();

    const staffId = record.userId || record.user?.id || record.id;

    noteForm.setFieldsValue({
      staffId: staffId,
      date: dayjs(record.date),
      note: record.note || '',
    });
    setNoteOpen(true);
  };

  const closeNoteModal = () => {
    setNoteOpen(false);
    setSelectedRecord(null);
    noteForm.resetFields();
  };

  const submitNote = async () => {
    try {
      const values = await noteForm.validateFields();

      if (!values.staffId) {
        message.error('Staff ID is missing');
        return;
      }

      const payload = {
        staffId: values.staffId,
        date: values.date.format('YYYY-MM-DD'),
        note: values.note,
      };

      await api.post('/admin/attendance/note', payload);
      message.success('Note saved successfully');
      closeNoteModal();
      fetchAttendance();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to save note');
    }
  };

  const getStatusClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (['present', 'overtime'].includes(s)) return 'sales-status-complete';
    if (['absent'].includes(s)) return 'sales-status-inactive';
    if (['half_day', 'pending_approval'].includes(s)) return 'sales-status-pending';
    if (['leave', 'on_leave'].includes(s)) return 'sales-status-active';
    if (['weekly_off'].includes(s)) return 'sales-status-inprogress';
    return 'sales-status-active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'green';
      case 'overtime': return 'green';
      case 'absent': return 'red';
      case 'half_day': return 'orange';
      case 'pending_approval': return 'orange';
      case 'leave': return 'blue';
      case 'on_leave': return 'blue';
      default: return 'default';
    }
  };

  const baseForCounts = attendance.filter(row => {
    const q = search.trim().toLowerCase();
    const matchesSearch = q
      ? (row.user?.name || '').toLowerCase().includes(q) ||
      (row.staffProfile?.staffId || '').toString().toLowerCase().includes(q)
      : true;
    const matchesDept = departmentFilter === 'all' ? true : (row.staffProfile?.department || '') === departmentFilter;
    const matchesDate = dateFilter ? row.date === dateFilter.format('YYYY-MM-DD') : true;
    return matchesSearch && matchesDept && matchesDate;
  });

  const filteredActiveStaff = staffList.filter(s => {
    if (!s.active) return false;
    const q = search.trim().toLowerCase();
    const matchesSearch = q
      ? (s.name || '').toLowerCase().includes(q) ||
      (s.staffId || '').toString().toLowerCase().includes(q)
      : true;
    const matchesDept = departmentFilter === 'all' ? true : (s.department || '') === departmentFilter;
    return matchesSearch && matchesDept;
  });

  const activeStaffCount = filteredActiveStaff.length;
  const presentCount = baseForCounts.filter(a => ['present', 'overtime', 'half_day'].includes(a.status?.toLowerCase())).length;

  const attendanceLeaveUserIds = new Set(
    baseForCounts.filter(a => a.status?.toLowerCase() === 'leave').map(a => a.userId)
  );
  const approvedLeaveUserIds = new Set(
    approvedLeaves
      .filter(l => selectedStaff === 'all' || String(l.userId) === String(selectedStaff))
      .map(l => l.userId)
  );
  const allLeaveUserIds = new Set([...attendanceLeaveUserIds, ...approvedLeaveUserIds]);
  const leaveCount = allLeaveUserIds.size;
  const absentCount = Math.max(0, activeStaffCount - presentCount - leaveCount);

  const attendanceDateStr = selectedDate?.format('YYYY-MM-DD');
  const attendanceUserIdSet = new Set(attendance.map(a => a.userId));
  const leaveRows = approvedLeaves
    .filter(l => !attendanceUserIdSet.has(l.userId) && !attendanceUserIdSet.has(Number(l.userId)))
    .map(l => {
      const staffInfo = staffList.find(s => s.id === l.userId || s.id === Number(l.userId));
      return {
        id: `leave-req-${l.id}`,
        userId: l.userId,
        date: attendanceDateStr,
        status: 'leave',
        checkIn: null,
        checkOut: null,
        breakTotalSeconds: null,
        totalWorkHours: null,
        user: { name: l.user?.profile?.name || staffInfo?.name || 'Unknown', id: l.userId },
        staffProfile: {
          staffId: l.user?.profile?.staffId || staffInfo?.staffId || 'N/A',
          department: staffInfo?.department || l.user?.profile?.department || '',
        },
        _isLeaveRequest: true,
        leaveType: l.leaveType,
        categoryKey: l.categoryKey,
        categoryName: l.categoryName,
      };
    });

  const allRows = [...attendance, ...leaveRows];

  const filtered = allRows.filter(row => {
    const q = search.trim().toLowerCase();
    const matchesSearch = q
      ? (row.user?.name || '').toLowerCase().includes(q) ||
      (row.staffProfile?.staffId || '').toString().toLowerCase().includes(q)
      : true;
    const matchesStatus = statusFilter === 'all' ? true : row.status === statusFilter;
    const matchesDept = departmentFilter === 'all' ? true : (row.staffProfile?.department || '') === departmentFilter;
    const matchesStaffName = staffNameFilter === 'all' ? true : (row.user?.name || '') === staffNameFilter;
    const matchesDate = dateFilter ? row.date === dateFilter.format('YYYY-MM-DD') : true;
    return matchesSearch && matchesStatus && matchesDept && matchesStaffName && matchesDate;
  });

  const columns = [
    {
      title: 'Staff Name',
      key: 'staffName',
      render: (_, record) => {
        const name = record.user?.name || 'Unknown';
        const staffId = record.staffProfile?.staffId || 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: '#e6f7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              color: '#1677ff',
              fontSize: '16px',
              fontWeight: '700',
              boxShadow: '0 2px 6px rgba(22, 119, 255, 0.08)'
            }}>
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1677ff' }}>{name}</div>
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>ID: {staffId}</div>
              <Space size={8} style={{ marginTop: '4px' }}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => openNoteModal(record)}
                  style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                >
                  {record.note ? 'Edit Note' : '+ Add Note'}
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setLogsData(record);
                    setLogsModalOpen(true);
                  }}
                  style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                >
                  View Logs
                </Button>
                {record.id && !String(record.id).startsWith('leave-req-') && (
                  <Popconfirm
                    title="Delete Attendance"
                    description="Are you sure you want to delete this attendance record?"
                    onConfirm={() => deleteAttendance(record.id)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Department',
      dataIndex: ['staffProfile', 'department'],
      key: 'department',
      render: (dept) => dept || '-',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Check In',
      dataIndex: 'checkIn',
      key: 'checkIn',
      render: (time, record) => {
        if (!time) return '-';
        if (String(record.status || '').toLowerCase() === 'pending_approval') { return <Tag color="warning" style={{ fontWeight: 600 }}>Approval Pending</Tag>; }
        if (record.isRmo && record.punchedInAt) {
          const punchInDate = dayjs(record.punchedInAt).format('YYYY-MM-DD');
          if (punchInDate !== record.date) {
            return `${time} (${dayjs(record.punchedInAt).format('DD MMM')})`;
          }
        }
        return time;
      },
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOut',
      key: 'checkOut',
      render: (time, record) => {
        if (!time) return '-';
        if (String(record.status || '').toLowerCase() === 'pending_approval') { return <Tag color="warning" style={{ fontWeight: 600 }}>Approval Pending</Tag>; }
        if (record.isRmo && record.punchedOutAt) {
          const punchOutDate = dayjs(record.punchedOutAt).format('YYYY-MM-DD');
          if (punchOutDate !== record.date) {
            return `${time} (${dayjs(record.punchedOutAt).format('DD MMM')})`;
          }
        }
        return time;
      },
    },
    {
      title: 'Break',
      dataIndex: 'breakTotalSeconds',
      key: 'breakTotalSeconds',
      render: (seconds) => {
        if (seconds && seconds > 0) {
          return `${(seconds / 3600).toFixed(2)}h`;
        }
        return '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (String(status || '').toLowerCase() === 'pending_approval') {
          return (
            <Space direction="vertical" size={2}>
              <Tag color="orange" style={{ fontWeight: 700, padding: '2px 8px' }}>
                PENDING APPROVAL
              </Tag>
              {record.latePunchInMinutes > 0 && (
                <Tag color="error" style={{ fontSize: '10px', marginTop: 2 }}>
                  Late by {record.latePunchInMinutes} min
                </Tag>
              )}
            </Space>
          );
        }
        const isLeave = ['leave', 'on_leave'].includes(String(status || '').toLowerCase());
        const leaveCategoryLabel = record.categoryName || record.categoryKey || record.leaveType;
        let statusLabel = isLeave && leaveCategoryLabel
          ? `Leave (${String(leaveCategoryLabel).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())})`
          : (status?.replace('_', ' ').toUpperCase() || 'ABSENT');

        if (String(status || '').toLowerCase() === 'weekly_off' && record.source === 'roster') {
          statusLabel = 'WEEKLY OFF (Assign from roster)';
        }

        return (
          <Space direction="vertical" size={2}>
            <span className={`sales-status-tag ${getStatusClass(status)}`}>
              {statusLabel}
            </span>
            {status === 'overtime' && record.overtimeMinutes > 0 && (
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 2 }}>
                ({record.overtimeMinutes} min OT)
              </Text>
            )}
            {record.isLate && record.latePenaltyText && (
              <Tag color="error" style={{ fontSize: '10px', marginTop: 2, whiteSpace: 'normal', height: 'auto', padding: '2px 4px' }}>
                Late: {record.latePenaltyText}
                {record.bufferMinutes > 0 && ` (Buffer: ${record.bufferMinutes}m applied)`}
              </Tag>
            )}
            {!record.latePenaltyText && record.latePunchInMinutes > 0 && (
              <Tag color="error" style={{ fontSize: '10px', marginTop: 2, whiteSpace: 'normal', height: 'auto', padding: '2px 4px' }}>
                Late by {record.latePunchInMinutes} min
                {record.bufferMinutes > 0 && ` (Buffer: ${record.bufferMinutes}m applicable)`}
              </Tag>
            )}
            {record.earlyExitMinutes > 0 && (
              <Tag color="warning" style={{ fontSize: '10px', marginTop: 2, whiteSpace: 'normal', height: 'auto', padding: '2px 4px' }}>
                Early Exit: {record.earlyExitMinutes} min
              </Tag>
            )}
            {record.earlyOvertimeMinutes > 0 && (
              <Tag color="cyan" style={{ fontSize: '10px', marginTop: 2, whiteSpace: 'normal', height: 'auto', padding: '2px 4px' }}>
                Early OT: {record.earlyOvertimeMinutes} min
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Working Hours',
      key: 'workingHours',
      render: (_, record) => {
        return formatWorkingHours(record.checkIn, record.checkOut, record.totalWorkHours);
      },
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => {
        const hasLocation = record.latitude || record.punchOutLatitude;
        if (!hasLocation) return '-';

        return (
          <Button
            type="text"
            className="sales-action-btn"
            icon={<EnvironmentOutlined style={{ color: '#1677ff', fontSize: '16px' }} />}
            onClick={() => {
              if (subscriptionInfo?.attendanceLocationEnabled) {
                setLocationData(record);
                setLocationModalOpen(true);
              } else {
                message.warning("Subscription addon 'Attendance Location Details' is required to view locations");
              }
            }}
          />
        );
      },
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />

      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden' }}>
        <MainHeader 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          title="Attendance Management" 
        />

        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          
          {/* Top Stats Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={8}>
              <Card
                onClick={() => handleCardClick('present', 'Total Present')}
                style={{
                  background: '#ffffff',
                  border: '1px solid #f0f2f5',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out'
                }}
                className="dashboard-card-hover"
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Present</div>
                    <div style={{ color: '#1f1f1f', fontSize: '26px', fontWeight: '700', lineHeight: 1 }}>{presentCount}</div>
                  </div>
                  <div style={{
                    width: '46px',
                    height: '46px',
                    background: '#f6ffed',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(82, 196, 26, 0.1)'
                  }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                onClick={() => handleCardClick('absent', 'Absent')}
                style={{
                  background: '#ffffff',
                  border: '1px solid #f0f2f5',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out'
                }}
                className="dashboard-card-hover"
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Absent</div>
                    <div style={{ color: '#1f1f1f', fontSize: '26px', fontWeight: '700', lineHeight: 1 }}>{absentCount}</div>
                  </div>
                  <div style={{
                    width: '46px',
                    height: '46px',
                    background: '#fff1f0',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(255, 77, 79, 0.1)'
                  }}>
                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                onClick={() => handleCardClick('leave', 'On Leave')}
                style={{
                  background: '#ffffff',
                  border: '1px solid #f0f2f5',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out'
                }}
                className="dashboard-card-hover"
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ color: '#8c8c8c', fontSize: '13px', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>On Leave</div>
                    <div style={{ color: '#1f1f1f', fontSize: '26px', fontWeight: '700', lineHeight: 1 }}>{leaveCount}</div>
                  </div>
                  <div style={{
                    width: '46px',
                    height: '46px',
                    background: '#e6f7ff',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(24, 144, 255, 0.1)'
                  }}>
                    <CoffeeOutlined style={{ color: '#1677ff', fontSize: '20px' }} />
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card
            className="sales-content-card"
            bodyStyle={{ padding: '24px' }}
          >
            {/* Sleek Filter & Action Row */}
            <div className="sales-filter-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => setSelectedDate(date || dayjs())}
                  picker="date"
                  format="DD MMM YYYY"
                  placeholder="Select date"
                  style={{ width: 140 }}
                />
                <Input
                  placeholder="Search staff..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 180 }}
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  allowClear
                />
                <Select
                  value={departmentFilter}
                  onChange={setDepartmentFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">Department</Option>
                  {departments.map(dep => (
                    <Option key={dep.id || dep.name} value={dep.name}>{dep.name}</Option>
                  ))}
                </Select>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 120 }}
                >
                  <Option value="all">Status</Option>
                  <Option value="present">Present</Option>
                  <Option value="overtime">Overtime</Option>
                  <Option value="absent">Absent</Option>
                  <Option value="half_day">Half Day</Option>
                  <Option value="leave">Leave</Option>
                </Select>
                <Select
                  showSearch
                  value={staffNameFilter}
                  onChange={setStaffNameFilter}
                  style={{ width: 160 }}
                  placeholder="Staff Name"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="all">Staff Name</Option>
                  {staffList.map(staff => (
                    <Option key={staff.id} value={staff.name}>
                      {staff.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <Space wrap size={8}>
                <Button type="primary" onClick={() => openMarkModal()} shape="round">
                  Mark Attendance
                </Button>
                <Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }} onClick={openBulkMarkModal} shape="round">
                  Bulk Mark Attendance
                </Button>
                <Badge count={pendingRegCount + pendingLateCount} offset={[-3, 3]}>
                  <Button
                    type={(pendingRegCount + pendingLateCount) > 0 ? "primary" : "default"}
                    ghost={(pendingRegCount + pendingLateCount) > 0}
                    icon={<FileDoneOutlined />}
                    onClick={() => { fetchRegularizations(); fetchLateApprovals(); setRegModalOpen(true); }}
                    shape="round"
                    style={{
                      borderColor: (pendingRegCount + pendingLateCount) > 0 ? '#fa8c16' : undefined,
                      color: (pendingRegCount + pendingLateCount) > 0 ? '#fa8c16' : undefined,
                      fontWeight: 600
                    }}
                  >
                    Approvals & Regularization
                  </Button>
                </Badge>
                <Button icon={<ExportOutlined />} onClick={handleExport} shape="round">
                  Export
                </Button>
              </Space>
            </div>

            {selectedStaff !== 'all' && (
              <div style={{ marginBottom: 20, background: '#fafafa', padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                <Space size={24} wrap>
                  <span><strong>Effective Template:</strong> {effectiveTemplate?.name || '—'}</span>
                  <span><strong>Mode:</strong> {(effectiveTemplate?.attendanceMode || '').replace(/_/g, ' ') || '—'}</span>
                  <span><strong>Holidays:</strong> {effectiveTemplate?.holidaysRule || '—'}</span>
                  <span><strong>Track In/Out:</strong> {effectiveTemplate?.trackInOutEnabled ? 'Yes' : 'No'}</span>
                  <span><strong>Require Punch Out:</strong> {effectiveTemplate?.requirePunchOut ? 'Yes' : 'No'}</span>
                  <span><strong>Multiple Punches:</strong> {effectiveTemplate?.allowMultiplePunches ? 'Allowed' : 'Not Allowed'}</span>
                </Space>
              </div>
            )}

            <Table
              columns={columns}
              dataSource={filtered}
              loading={loading}
              rowKey="id"
              className="sales-table"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                  setPageSize(size);
                },
              }}
            />
          </Card>

          {/* Modals styled with .sales-modal */}
          <Modal
            title="Mark Attendance"
            open={markOpen}
            onCancel={() => setMarkOpen(false)}
            onOk={submitMark}
            okText="Save Record"
            className="sales-modal"
            destroyOnClose
          >
            <Form form={markForm} layout="vertical">
              <Form.Item name="staffId" label={<span className="modal-field-label">Select Staff Member</span>} rules={[{ required: true, message: 'Please select staff' }]} >
                <Select
                  showSearch
                  placeholder="Select staff"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onSelect={async (uid) => {
                    const dateStr = selectedDate.format('YYYY-MM-DD');

                    const isRmo = rmoStaffIds.map(Number).includes(Number(uid));
                    setIsRmoSelected(isRmo);

                    const staffInfo = staffList.find(s => s.id === uid || s.id === Number(uid));
                    if (staffInfo && staffInfo.active === false) {
                      setSelectedStaffDeactivated(true);
                      message.warning(`⚠️ ${staffInfo.name} is deactivated. Attendance can still be marked but this account is inactive.`);
                    } else {
                      setSelectedStaffDeactivated(false);
                    }

                    try {
                      const res = await api.get(`/admin/attendance/check-leave?userId=${uid}&date=${dateStr}`);
                      if (res.data.onLeave) {
                        setStaffOnLeave(true);
                        message.warning('today this staff is on paid leave');
                      } else {
                        setStaffOnLeave(false);
                      }
                    } catch (_) {
                      setStaffOnLeave(false);
                    }

                    fetchSpecialDayStatus(uid, dateStr);

                    const rec = attendance.find(
                      a => (a.userId === uid || a.userId === Number(uid)) && a.date === dateStr
                    );
                    const currentFormDate = markForm.getFieldValue('date') || selectedDate;
                    if (rec) {
                      const parsedIn = parseTimeValue(rec.checkIn) || parseTimeValue('09:30');
                      const parsedOut = parseTimeValue(rec.checkOut) || parseTimeValue('18:00');
                      const isNight = parsedIn && parsedOut && parsedOut.isBefore(parsedIn);

                      const inDate = rec.punchedInAt ? dayjs(rec.punchedInAt) : currentFormDate;
                      const outDate = rec.punchedOutAt ? dayjs(rec.punchedOutAt) : (isNight ? currentFormDate.add(1, 'day') : currentFormDate);

                      markForm.setFieldsValue({
                        status: rec.status || 'present',
                        checkIn: parsedIn,
                        checkOut: parsedOut,
                        checkInDate: inDate,
                        checkOutDate: outDate,
                      });
                    } else {
                      markForm.setFieldsValue({
                        status: 'present',
                        checkIn: parseTimeValue('09:30'),
                        checkOut: parseTimeValue('18:00'),
                        checkInDate: currentFormDate,
                        checkOutDate: currentFormDate,
                      });
                    }
                    api.get(`/admin/shifts/effective/${uid}?date=${dateStr}`).then(res => {
                      const shift = res.data?.shift;
                      setEffectiveShift(shift || null);
                      if (!rec && shift) {
                        const parsedIn = parseTimeValue(shift.startTime) || parseTimeValue('09:30');
                        const parsedOut = parseTimeValue(shift.endTime) || parseTimeValue('18:00');
                        const isNight = parsedIn && parsedOut && parsedOut.isBefore(parsedIn);

                        markForm.setFieldsValue({
                          checkIn: parsedIn,
                          checkOut: parsedOut,
                          checkInDate: currentFormDate,
                          checkOutDate: isNight ? currentFormDate.add(1, 'day') : currentFormDate,
                        });
                      }
                    }).catch(() => setEffectiveShift(null));
                  }}
                >
                  {Array.isArray(staffList) && staffList.map(s => (
                    <Option key={s.id} value={s.id}>{s.name} ({s.staffId || 'N/A'})</Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedStaffDeactivated && (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ff4d4f', borderRadius: 4, color: '#cf1322', fontWeight: 600 }}>
                  ⚠️ Warning: This staff member is currently <strong>deactivated</strong>. Their account is inactive in this organization.
                </div>
              )}

              {staffOnLeave && (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4, color: '#cf1322', fontWeight: 500 }}>
                  today this staff is on paid leave
                </div>
              )}

              {staffOffMsg && (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: 4, color: '#d46b08', fontWeight: 500 }}>
                  {staffOffMsg}
                </div>
              )}

              {isRmoSelected && (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4, color: '#0050b3', fontWeight: 500 }}>
                  ℹ️ This staff is RMO. Intermediate days are automatically marked as Present. You do not need to mark manually.
                </div>
              )}

              <Form.Item name="date" label={<span className="modal-field-label">Date</span>} rules={[{ required: true, message: 'Please choose date' }]} >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD MMM YYYY"
                  onChange={(d) => {
                    if (!d) return;
                    const uid = markForm.getFieldValue('staffId');
                    const dateStr = d.format('YYYY-MM-DD');
                    if (uid) {
                      const rec = attendance.find(
                        a => (a.userId === uid || a.userId === Number(uid)) && a.date === dateStr
                      );
                      if (rec) {
                        const parsedIn = parseTimeValue(rec.checkIn) || parseTimeValue('09:30');
                        const parsedOut = parseTimeValue(rec.checkOut) || parseTimeValue('18:00');
                        const isNight = parsedIn && parsedOut && parsedOut.isBefore(parsedIn);

                        const inDate = rec.punchedInAt ? dayjs(rec.punchedInAt) : d;
                        const outDate = rec.punchedOutAt ? dayjs(rec.punchedOutAt) : (isNight ? d.add(1, 'day') : d);

                        markForm.setFieldsValue({
                          status: rec.status || 'present',
                          checkIn: parsedIn,
                          checkOut: parsedOut,
                          checkInDate: inDate,
                          checkOutDate: outDate,
                        });
                      } else {
                        markForm.setFieldsValue({
                          checkInDate: d,
                          checkOutDate: d,
                        });
                      }
                    } else {
                      markForm.setFieldsValue({
                        checkInDate: d,
                        checkOutDate: d,
                      });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name="status" label={<span className="modal-field-label">Status</span>} rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value="present">Present</Radio>
                  <Radio value="overtime">Overtime</Radio>
                  <Radio value="absent">Absent</Radio>
                  <Radio value="half_day">Half Day</Radio>
                  <Radio value="leave">Leave</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.status !== cur.status}>
                {({ getFieldValue }) => {
                  const status = getFieldValue('status');
                  const hasAutoOT = effectiveShift && Number(effectiveShift.overtimeStartMinutes) > 0;
                  if (status === 'overtime' && !hasAutoOT) {
                    return (
                      <Form.Item
                        name="overtimeMinutes"
                        label={<span className="modal-field-label">Overtime Minutes</span>}
                        rules={[{ required: false }]}
                      >
                        <AntInput type="number" min={0} placeholder="Enter OT minutes if needed" />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="checkInDate" label={<span className="modal-field-label">Check-in Date</span>}>
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="checkIn" label={<span className="modal-field-label">Check-in Time</span>}>
                    <TimePicker style={{ width: '100%' }} format="HH:mm" needConfirm={false} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="checkOutDate" label={<span className="modal-field-label">Check-out Date</span>}>
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="checkOut" label={<span className="modal-field-label">Check-out Time</span>}>
                    <TimePicker style={{ width: '100%' }} format="HH:mm" needConfirm={false} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>

          <Modal
            title="Bulk Mark Attendance"
            open={bulkMarkOpen}
            onCancel={() => { setBulkMarkOpen(false); setBulkRows([]); }}
            onOk={submitBulkMark}
            okText="Save Bulk Attendance"
            width={1050}
            className="sales-modal"
            destroyOnClose
          >
            {/* Date Selection Section */}
            <div style={{ marginBottom: 20, background: '#fafafa', padding: 16, borderRadius: 10, border: '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: 10, fontWeight: 600, color: '#1e293b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CalendarOutlined style={{ color: '#1677ff' }} /> Select Date(s) Option:
              </div>
              <Radio.Group
                value={bulkDateMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  setBulkDateMode(newMode);
                  syncBulkRows(bulkStaffIds, getBulkSelectedDates(newMode));
                }}
                buttonStyle="solid"
                size="middle"
                style={{ marginBottom: 14 }}
              >
                <Radio.Button value="single">Single Date</Radio.Button>
                <Radio.Button value="range">Date Range (From - To)</Radio.Button>
                <Radio.Button value="multiple">Multiple Custom Dates</Radio.Button>
              </Radio.Group>

              {/* Single Date */}
              {bulkDateMode === 'single' && (
                <div>
                  <span className="modal-field-label" style={{ display: 'block', marginBottom: 4 }}>Date</span>
                  <DatePicker
                    value={bulkDate}
                    onChange={(d) => {
                      setBulkDate(d);
                      if (!d) return;
                      syncBulkRows(bulkStaffIds, [d]);
                    }}
                    format="DD MMM YYYY"
                    style={{ width: 220 }}
                  />
                </div>
              )}

              {/* Date Range */}
              {bulkDateMode === 'range' && (
                <div>
                  <span className="modal-field-label" style={{ display: 'block', marginBottom: 4 }}>Select Date Range</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <DatePicker.RangePicker
                      value={bulkDateRange}
                      onChange={(dates) => {
                        setBulkDateRange(dates);
                        if (dates && dates[0] && dates[1]) {
                          syncBulkRows(bulkStaffIds, getBulkSelectedDates('range', null, dates));
                        }
                      }}
                      format="DD MMM YYYY"
                      style={{ width: 340 }}
                    />
                    {bulkDateRange && bulkDateRange[0] && bulkDateRange[1] && (
                      <Tag color="blue" style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, fontWeight: 600 }}>
                        📅 {getBulkSelectedDates().length} Days Selected ({bulkDateRange[0].format('DD MMM')} to {bulkDateRange[1].format('DD MMM YYYY')})
                      </Tag>
                    )}
                  </div>
                </div>
              )}

              {/* Multiple Custom Dates */}
              {bulkDateMode === 'multiple' && (
                <div>
                  <span className="modal-field-label" style={{ display: 'block', marginBottom: 4 }}>Pick Dates to Add</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <DatePicker
                      format="DD MMM YYYY"
                      placeholder="Click to pick & add a date"
                      onChange={(d) => {
                        if (!d) return;
                        if (!bulkMultipleDates.some(existing => existing.isSame(d, 'day'))) {
                          const newMulti = [...bulkMultipleDates, d];
                          setBulkMultipleDates(newMulti);
                          syncBulkRows(bulkStaffIds, getBulkSelectedDates('multiple', null, null, newMulti));
                        }
                      }}
                      style={{ width: 220 }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        const initMulti = [dayjs()];
                        setBulkMultipleDates(initMulti);
                        syncBulkRows(bulkStaffIds, initMulti);
                      }}
                      style={{ fontSize: 12 }}
                    >
                      Reset Dates
                    </Button>
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {bulkMultipleDates.map((d, idx) => (
                      <Tag
                        key={d.format('YYYY-MM-DD')}
                        color="geekblue"
                        closable={bulkMultipleDates.length > 1}
                        onClose={() => {
                          const newMulti = bulkMultipleDates.filter((_, i) => i !== idx);
                          setBulkMultipleDates(newMulti);
                          syncBulkRows(bulkStaffIds, getBulkSelectedDates('multiple', null, null, newMulti));
                        }}
                        style={{ padding: '4px 10px', fontSize: 13, borderRadius: 6, fontWeight: 500 }}
                      >
                        {d.format('DD MMM YYYY')}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Exclusion Checkboxes */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #cbd5e1', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>🚫 Skip/Exclude:</span>
                <Checkbox
                  checked={excludeWeeklyOff}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setExcludeWeeklyOff(val);
                    syncBulkRows(bulkStaffIds, getBulkSelectedDates(), { excludeWeeklyOff: val });
                  }}
                  style={{ fontSize: 13, fontWeight: 500 }}
                >
                  Exclude Weekly Off
                </Checkbox>
                <Checkbox
                  checked={excludeHoliday}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setExcludeHoliday(val);
                    syncBulkRows(bulkStaffIds, getBulkSelectedDates(), { excludeHoliday: val });
                  }}
                  style={{ fontSize: 13, fontWeight: 500 }}
                >
                  Exclude Holiday
                </Checkbox>
                <Checkbox
                  checked={excludePaidLeave}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setExcludePaidLeave(val);
                    syncBulkRows(bulkStaffIds, getBulkSelectedDates(), { excludePaidLeave: val });
                  }}
                  style={{ fontSize: 13, fontWeight: 500 }}
                >
                  Exclude Paid Leave
                </Checkbox>
              </div>
            </div>

            {/* Staff selector */}
            <div style={{ marginBottom: 16 }}>
              <span className="modal-field-label">Select Staff Members</span>
              <Select
                mode="multiple"
                showSearch
                placeholder="Select staff to add rows below"
                style={{ width: '100%', marginTop: 6 }}
                value={bulkStaffIds}
                onSelect={handleBulkStaffSelect}
                onDeselect={handleBulkStaffDeselect}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {Array.isArray(staffList) && staffList.filter(s => !rmoStaffIds.map(Number).includes(Number(s.id))).map(s => (
                  <Option key={s.id} value={s.id}>{s.name} ({s.staffId || 'N/A'})</Option>
                ))}
              </Select>
            </div>

            {/* Summary Box & Master Quick Fill */}
            {bulkRows.length > 0 && (
              <div style={{ marginBottom: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                <div style={{ marginBottom: 10, fontSize: 13, color: '#0050b3', fontWeight: 600 }}>
                  📌 Bulk Attendance Breakdown: {bulkStaffIds.length} Staff Member(s) × {getBulkSelectedDates().length} Date(s) = <strong>{bulkRows.length} Total Entry Row(s)</strong> below.
                </div>

                {/* Quick Master Pre-fill Bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px dashed #cbd5e1' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>⚡ Quick Pre-Fill All Rows:</span>
                  <Select
                    size="small"
                    value={masterStatus}
                    onChange={(v) => setMasterStatus(v)}
                    style={{ width: 110 }}
                  >
                    <Option value="present">Present</Option>
                    <Option value="overtime">Overtime</Option>
                    <Option value="absent">Absent</Option>
                    <Option value="half_day">Half Day</Option>
                    <Option value="leave">Leave</Option>
                  </Select>
                  <TimePicker
                    size="small"
                    value={masterCheckIn}
                    format="HH:mm"
                    needConfirm={false}
                    onChange={(v) => setMasterCheckIn(v)}
                    style={{ width: 95 }}
                  />
                  <TimePicker
                    size="small"
                    value={masterCheckOut}
                    format="HH:mm"
                    needConfirm={false}
                    onChange={(v) => setMasterCheckOut(v)}
                    style={{ width: 95 }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={applyMasterToAll}
                    style={{ borderRadius: 6, fontWeight: 600, fontSize: 12 }}
                  >
                    Apply to All {bulkRows.length} Rows
                  </Button>
                </div>
              </div>
            )}

            {/* Per-date-staff editable table */}
            {bulkRows.length > 0 && (
              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', position: 'sticky', top: 0, zIndex: 2 }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Staff Name</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Check-in Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Check-in Time</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Check-out Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#334155' }}>Check-out Time</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#334155' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map(row => (
                      <tr key={row.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                          <Tag color="blue" style={{ fontWeight: 600, margin: 0 }}>{row.formattedDate}</Tag>
                        </td>
                        <td style={{ padding: '10px 12px', fontWeight: 500, color: '#1e293b' }}>{row.staffName}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <Select
                            size="small"
                            value={row.status}
                            onChange={v => updateBulkRowByKey(row.key, 'status', v)}
                            style={{ width: 100 }}
                          >
                            <Option value="present">Present</Option>
                            <Option value="overtime">Overtime</Option>
                            <Option value="absent">Absent</Option>
                            <Option value="half_day">Half Day</Option>
                            <Option value="leave">Leave</Option>
                          </Select>
                          {row.status === 'overtime' && !row.hasAutoOT ? (
                            <AntInput
                              type="number"
                              min={0}
                              placeholder="OT min"
                              value={row.overtimeMinutes ?? ''}
                              onChange={(e) => updateBulkRowByKey(row.key, 'overtimeMinutes', e.target.value)}
                              style={{ width: 100, marginTop: 4, display: 'block' }}
                            />
                          ) : null}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <DatePicker
                            size="small"
                            value={row.checkInDate}
                            format="DD MMM YYYY"
                            onChange={d => updateBulkRowByKey(row.key, 'checkInDate', d)}
                            style={{ width: 125 }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <TimePicker
                            size="small"
                            value={row.checkIn}
                            format="HH:mm"
                            needConfirm={false}
                            onChange={v => updateBulkRowByKey(row.key, 'checkIn', v)}
                            style={{ width: 95 }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <DatePicker
                            size="small"
                            value={row.checkOutDate}
                            format="DD MMM YYYY"
                            onChange={d => updateBulkRowByKey(row.key, 'checkOutDate', d)}
                            style={{ width: 125 }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <TimePicker
                            size="small"
                            value={row.checkOut}
                            format="HH:mm"
                            needConfirm={false}
                            onChange={v => updateBulkRowByKey(row.key, 'checkOut', v)}
                            style={{ width: 95 }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeBulkRowByKey(row.key)}
                            title="Remove this date row"
                            style={{ borderRadius: 6 }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {bulkRows.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>Select staff members and date(s) above to add breakdown rows</div>
            )}
          </Modal>

          <Modal
            title={selectedRecord?.note ? 'Edit Note' : 'Add Note'}
            open={noteOpen}
            onCancel={closeNoteModal}
            onOk={submitNote}
            okText={selectedRecord?.note ? 'Update Note' : 'Save Note'}
            width={500}
            className="sales-modal"
            destroyOnClose
          >
            <Form form={noteForm} layout="vertical">
              <Form.Item name="staffId" label="Staff Member" style={{ display: "none" }}>
                <AntInput disabled />
              </Form.Item>
              <Form.Item name="date" label={<span className="modal-field-label">Date</span>}>
                <DatePicker style={{ width: '100%' }} disabled />
              </Form.Item>
              <Form.Item
                name="note"
                label={<span className="modal-field-label">Note</span>}
                rules={[{ required: true, message: 'Please enter a note' }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter your note here..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Location Details"
            open={locationModalOpen}
            onCancel={() => setLocationModalOpen(false)}
            footer={[
              <Button key="close" onClick={() => setLocationModalOpen(false)} shape="round">Close</Button>
            ]}
            width={700}
            className="sales-modal"
            destroyOnClose
          >
            {locationData && (
              <div style={{ padding: '10px 0' }}>
                <div style={{ marginBottom: 20, paddingBottom: 15, borderBottom: '1px solid #f0f0f0' }}>
                  <Title level={5}><UserOutlined style={{ color: '#1677ff' }} /> {locationData.user?.name || 'Staff'}</Title>
                  <Space direction="vertical" size={2}>
                    <Text type="secondary">ID: {locationData.staffProfile?.staffId || 'N/A'}</Text>
                    {locationData.staffProfile?.phone && (
                      <Text><PhoneOutlined /> {locationData.staffProfile.phone}</Text>
                    )}
                  </Space>
                </div>

                <div style={{ display: 'flex', gap: 20 }}>
                  {/* Punch In */}
                  <div style={{ flex: 1, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
                    <Title level={5} style={{ color: '#52c41a' }}><CheckCircleOutlined /> Punch In</Title>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Address:</Text>
                      <div style={{ marginTop: 4, fontSize: '13px' }}>{locationData.address || 'No address recorded'}</div>
                    </div>
                    {Number.isFinite(Number(locationData.latitude)) && Number.isFinite(Number(locationData.longitude)) && (
                      <div>
                        <Text strong>Coordinates:</Text>
                        <div style={{ marginTop: 4, color: '#1677ff', fontSize: '13px' }}>
                          <a
                            href={`https://www.google.com/maps?q=${locationData.latitude},${locationData.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {Number(locationData.latitude).toFixed(6)}, {Number(locationData.longitude).toFixed(6)}
                            <ExportOutlined style={{ marginLeft: 4 }} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Punch Out */}
                  <div style={{ flex: 1, padding: 12, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8 }}>
                    <Title level={5} style={{ color: '#fa8c16' }}><LogoutOutlined /> Punch Out</Title>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Address:</Text>
                      <div style={{ marginTop: 4, fontSize: '13px' }}>{locationData.punchOutAddress || 'No address recorded'}</div>
                    </div>
                    {Number.isFinite(Number(locationData.punchOutLatitude)) && Number.isFinite(Number(locationData.punchOutLongitude)) && (
                      <div>
                        <Text strong>Coordinates:</Text>
                        <div style={{ marginTop: 4, color: '#1677ff', fontSize: '13px' }}>
                          <a
                            href={`https://www.google.com/maps?q=${locationData.punchOutLatitude},${locationData.punchOutLongitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {Number(locationData.punchOutLatitude).toFixed(6)}, {Number(locationData.punchOutLongitude).toFixed(6)}
                            <ExportOutlined style={{ marginLeft: 4 }} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal>

          <Modal
            title={`Attendance Logs - ${logsData?.user?.name || 'Staff'}`}
            open={logsModalOpen}
            onCancel={() => setLogsModalOpen(false)}
            footer={[
              <Button key="close" onClick={() => setLogsModalOpen(false)} shape="round">Close</Button>
            ]}
            width={700}
            className="sales-modal"
            destroyOnClose
          >
            {logsData && (
              <div style={{ padding: '10px 0' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Card size="small" title={<span style={{ fontWeight: 600 }}>Punch In Photo</span>} bordered={false} style={{ background: '#fafafa', borderRadius: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        {logsData.punchInPhotoUrl ? (
                          <Image
                            src={logsData.punchInPhotoUrl.startsWith('http') ? logsData.punchInPhotoUrl : `${api.defaults.baseURL}${logsData.punchInPhotoUrl}`}
                            alt="Punch In"
                            style={{ maxWidth: '100%', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ padding: '20px', background: '#f0f0f0', borderRadius: '4px', color: '#8c8c8c' }}>
                            No punch-in photo
                          </div>
                        )}
                        <div style={{ marginTop: '10px' }}>
                          <Text strong>Time: </Text>
                          <Text>{logsData.checkIn || '-'}</Text>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title={<span style={{ fontWeight: 600 }}>Punch Out Photo</span>} bordered={false} style={{ background: '#fafafa', borderRadius: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        {logsData.punchOutPhotoUrl ? (
                          <Image
                            src={logsData.punchOutPhotoUrl.startsWith('http') ? logsData.punchOutPhotoUrl : `${api.defaults.baseURL}${logsData.punchOutPhotoUrl}`}
                            alt="Punch Out"
                            style={{ maxWidth: '100%', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ padding: '20px', background: '#f0f0f0', borderRadius: '4px', color: '#8c8c8c' }}>
                            No punch-out photo
                          </div>
                        )}
                        <div style={{ marginTop: '10px' }}>
                          <Text strong>Time: </Text>
                          <Text>{logsData.checkOut || '-'}</Text>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </Modal>

          {/* Card Employee List Modal */}
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '24px' }}>
                <span style={{ fontSize: '18px', fontWeight: 600 }}>{cardModalTitle} ({cardModalData.length})</span>
              </div>
            }
            open={cardModalVisible}
            onCancel={() => setCardModalVisible(false)}
            footer={[
              <Button key="close" type="primary" onClick={() => setCardModalVisible(false)}>
                Close
              </Button>
            ]}
            width={750}
            destroyOnClose
          >
            <div style={{ marginBottom: '16px', marginTop: '12px' }}>
              <Input
                placeholder="Search employee by name, phone, department..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={cardSearchText}
                onChange={(e) => setCardSearchText(e.target.value)}
                allowClear
              />
            </div>
            {cardModalLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                dataSource={cardModalData.filter(item => {
                  if (!cardSearchText) return true;
                  const q = cardSearchText.toLowerCase();
                  return (
                    (item.name && item.name.toLowerCase().includes(q)) ||
                    (item.phone && item.phone.toLowerCase().includes(q)) ||
                    (item.department && item.department.toLowerCase().includes(q)) ||
                    (item.designation && item.designation.toLowerCase().includes(q))
                  );
                })}
                rowKey="id"
                pagination={{ pageSize: 7, showSizeChanger: false }}
                size="small"
                columns={[
                  {
                    title: 'Staff Member',
                    key: 'name',
                    render: (record) => (
                      <div>
                        <div style={{ fontWeight: 600, color: '#262626' }}>{record.name}</div>
                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.phone} {record.staffId ? `• ${record.staffId}` : ''}</div>
                      </div>
                    )
                  },
                  {
                    title: 'Department',
                    dataIndex: 'department',
                    key: 'department',
                    render: (val) => <Tag color="blue">{val || 'General'}</Tag>
                  },
                  {
                    title: 'Designation',
                    dataIndex: 'designation',
                    key: 'designation',
                    render: (val) => val || 'Staff'
                  },
                  ...(cardModalType === 'present' ? [
                    {
                      title: 'Punch In',
                      dataIndex: 'punchedInAt',
                      key: 'punchedInAt',
                      render: (val) => <Tag color="green">{val || 'Present'}</Tag>
                    },
                    {
                      title: 'Punch Out',
                      dataIndex: 'punchedOutAt',
                      key: 'punchedOutAt',
                      render: (val) => <Tag color={val !== '-' ? 'blue' : 'default'}>{val || '-'}</Tag>
                    }
                  ] : []),
                  ...(cardModalType === 'leave' ? [
                    {
                      title: 'Leave Type',
                      dataIndex: 'leaveType',
                      key: 'leaveType',
                      render: (val) => <Tag color="purple">{val}</Tag>
                    },
                    {
                      title: 'Reason / Note',
                      dataIndex: 'reason',
                      key: 'reason'
                    }
                  ] : []),
                  ...(cardModalType === 'absent' ? [
                    {
                      title: 'Today Status',
                      key: 'absentStatus',
                      render: () => <Tag color="red">Absent Today</Tag>
                    }
                  ] : [])
                ]}
              />
            )}
          </Modal>

          {/* Regularization Verification Modal */}
          <Modal
            title={
              <Space>
                <FileDoneOutlined style={{ color: '#2563eb' }} />
                <span>Attendance Approvals & Regularization</span>
                {(pendingRegCount + pendingLateCount) > 0 && (
                  <Tag color="orange">{(pendingRegCount + pendingLateCount)} Pending</Tag>
                )}
              </Space>
            }
            open={regModalOpen}
            onCancel={() => setRegModalOpen(false)}
            width={1050}
            footer={[
              <Button key="close" onClick={() => setRegModalOpen(false)}>
                Close
              </Button>
            ]}
            destroyOnClose
          >
            <Tabs
              activeKey={regModalActiveTab}
              onChange={setRegModalActiveTab}
              items={[
                {
                  key: 'regularization',
                  label: (
                    <span>
                      <FileDoneOutlined style={{ marginRight: 6 }} />
                      Regularization Requests
                      {pendingRegCount > 0 && <Badge count={pendingRegCount} style={{ marginLeft: 6, backgroundColor: '#fa8c16' }} />}
                    </span>
                  ),
                  children: (
                    <div>
                      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Radio.Group
                          value={regStatusFilter}
                          onChange={(e) => setRegStatusFilter(e.target.value)}
                          buttonStyle="solid"
                        >
                          <Radio.Button value="ALL">All ({regList.length})</Radio.Button>
                          <Radio.Button value="PENDING">Pending ({regList.filter(r => r.status === 'PENDING').length})</Radio.Button>
                          <Radio.Button value="APPROVED">Approved ({regList.filter(r => r.status === 'APPROVED').length})</Radio.Button>
                          <Radio.Button value="REJECTED">Rejected ({regList.filter(r => r.status === 'REJECTED').length})</Radio.Button>
                        </Radio.Group>

                        <Button onClick={fetchRegularizations} loading={regLoading}>
                          Refresh
                        </Button>
                      </div>

                      <Table
                        loading={regLoading}
                        dataSource={regList.filter(r => regStatusFilter === 'ALL' || r.status === regStatusFilter)}
                        rowKey="id"
                        pagination={{ pageSize: 8 }}
                        size="middle"
                        columns={[
                          {
                            title: 'Staff Details',
                            key: 'staff',
                            render: (_, r) => (
                              <div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                  {r.user?.profile?.name || r.user?.phone || 'Staff'}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  ID: {r.user?.profile?.staffId || '--'} • {r.user?.profile?.department || 'General'}
                                </div>
                              </div>
                            )
                          },
                          {
                            title: 'Date',
                            dataIndex: 'date',
                            key: 'date',
                            render: (d) => <Text strong>{d}</Text>
                          },
                          {
                            title: 'Requested Timings',
                            key: 'timings',
                            render: (_, r) => (
                              <div style={{ fontSize: 12 }}>
                                <div>In: <strong>{r.requestedInTime || '--'}</strong></div>
                                <div>Out: <strong>{r.requestedOutTime || '--'}</strong></div>
                              </div>
                            )
                          },
                          {
                            title: 'Reason',
                            dataIndex: 'reason',
                            key: 'reason',
                            render: (t) => <span style={{ color: '#475569', fontSize: 12 }}>{t || '--'}</span>
                          },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (s) => {
                              const col = s === 'APPROVED' ? 'green' : (s === 'REJECTED' ? 'red' : 'orange');
                              return <Tag color={col} style={{ fontWeight: 600 }}>{s}</Tag>;
                            }
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            width: 200,
                            render: (_, r) => {
                              if (r.status === 'PENDING') {
                                return (
                                  <Space size={8}>
                                    <Popconfirm
                                      title="Approve Regularization?"
                                      description={'This will mark attendance as PRESENT for ' + r.date + ' with requested timings.'}
                                      onConfirm={() => handleReviewRegularization(r.id, 'APPROVED')}
                                      okText="Yes, Approve"
                                      cancelText="Cancel"
                                    >
                                      <Button
                                        type="primary"
                                        size="small"
                                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                        loading={reviewingId === r.id}
                                      >
                                        Approve
                                      </Button>
                                    </Popconfirm>

                                    <Button
                                      danger
                                      size="small"
                                      onClick={() => {
                                        setRejectingRecord(r);
                                        setRejectRemarks('');
                                        setRejectModalOpen(true);
                                      }}
                                      loading={reviewingId === r.id}
                                    >
                                      Reject
                                    </Button>
                                  </Space>
                                );
                              }
                              return (
                                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                  {r.adminRemarks ? ('Note: ' + r.adminRemarks) : (r.reviewedAt ? ('Reviewed ' + dayjs(r.reviewedAt).format('DD MMM')) : 'Completed')}
                                </span>
                              );
                            }
                          }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  key: 'late_approvals',
                  label: (
                    <span>
                      <ClockCircleOutlined style={{ marginRight: 6 }} />
                      Late Punch-In Approvals
                      {pendingLateCount > 0 && <Badge count={pendingLateCount} style={{ marginLeft: 6, backgroundColor: '#ff4d4f' }} />}
                    </span>
                  ),
                  children: (
                    <div>
                      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Radio.Group
                          value={lateStatusFilter}
                          onChange={(e) => setLateStatusFilter(e.target.value)}
                          buttonStyle="solid"
                        >
                          <Radio.Button value="ALL">All ({lateList.length})</Radio.Button>
                          <Radio.Button value="PENDING">Pending ({lateList.filter(r => r.status === 'PENDING').length})</Radio.Button>
                          <Radio.Button value="APPROVED">Approved ({lateList.filter(r => r.status === 'APPROVED').length})</Radio.Button>
                          <Radio.Button value="REJECTED">Rejected ({lateList.filter(r => r.status === 'REJECTED').length})</Radio.Button>
                        </Radio.Group>

                        <Button onClick={fetchLateApprovals} loading={lateLoading}>
                          Refresh
                        </Button>
                      </div>

                      <Table
                        loading={lateLoading}
                        dataSource={lateList.filter(r => lateStatusFilter === 'ALL' || r.status === lateStatusFilter)}
                        rowKey="id"
                        pagination={{ pageSize: 8 }}
                        size="middle"
                        columns={[
                          {
                            title: 'Staff Details',
                            key: 'staff',
                            render: (_, r) => (
                              <div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                  {r.user?.profile?.name || r.user?.phone || 'Staff'}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  ID: {r.user?.profile?.staffId || '--'} • {r.user?.profile?.department || 'General'}
                                </div>
                              </div>
                            )
                          },
                          {
                            title: 'Date',
                            dataIndex: 'date',
                            key: 'date',
                            render: (d) => <Text strong>{d}</Text>
                          },
                          {
                            title: 'Shift vs Punch Time',
                            key: 'timings',
                            render: (_, r) => (
                              <div style={{ fontSize: 12 }}>
                                <div>Shift Start: <strong>{r.shiftStartTime || '--'}</strong></div>
                                <div>Punched In: <strong style={{ color: '#cf1322' }}>{r.punchedInAt ? dayjs(r.punchedInAt).format('hh:mm A') : '--'}</strong></div>
                              </div>
                            )
                          },
                          {
                            title: 'Late By',
                            dataIndex: 'lateMinutes',
                            key: 'lateMinutes',
                            render: (mins) => (
                              <Tag color="error" style={{ fontWeight: 600 }}>
                                {mins} min late
                              </Tag>
                            )
                          },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (s) => {
                              const col = s === 'APPROVED' ? 'green' : (s === 'REJECTED' ? 'red' : 'orange');
                              return <Tag color={col} style={{ fontWeight: 600 }}>{s}</Tag>;
                            }
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            width: 200,
                            render: (_, r) => {
                              if (r.status === 'PENDING') {
                                return (
                                  <Space size={8}>
                                    <Popconfirm
                                      title="Approve Late Check-In?"
                                      description={'Confirm punch-in for ' + (r.user?.profile?.name || 'this staff') + ' on ' + r.date + '. Status will be marked PRESENT.'}
                                      onConfirm={() => handleReviewLateApproval(r.id, 'APPROVED')}
                                      okText="Yes, Approve"
                                      cancelText="Cancel"
                                    >
                                      <Button
                                        type="primary"
                                        size="small"
                                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                        loading={reviewingLateId === r.id}
                                      >
                                        Approve
                                      </Button>
                                    </Popconfirm>

                                    <Popconfirm
                                      title="Reject Late Check-In?"
                                      description="Punch-in will remain rejected/unapproved."
                                      onConfirm={() => handleReviewLateApproval(r.id, 'REJECTED')}
                                      okText="Yes, Reject"
                                      cancelText="Cancel"
                                    >
                                      <Button
                                        danger
                                        size="small"
                                        loading={reviewingLateId === r.id}
                                      >
                                        Reject
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                );
                              }
                              return (
                                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                  {r.adminRemarks ? ('Note: ' + r.adminRemarks) : (r.reviewedAt ? ('Reviewed ' + dayjs(r.reviewedAt).format('DD MMM')) : 'Completed')}
                                </span>
                              );
                            }
                          }
                        ]}
                      />
                    </div>
                  )
                }
              ]}
            />
          </Modal>

          {/* Rejection Remarks Modal */}
          <Modal
            title="Reject Regularization Request"
            open={rejectModalOpen}
            onCancel={() => { setRejectModalOpen(false); setRejectingRecord(null); }}
            onOk={() => handleReviewRegularization(rejectingRecord?.id, 'REJECTED', rejectRemarks)}
            okText="Confirm Reject"
            okButtonProps={{ danger: true }}
          >
            <p>Please enter rejection remarks / reason for <strong>{rejectingRecord?.user?.profile?.name || 'this staff'}</strong> on {rejectingRecord?.date}:</p>
            <Input.TextArea
              rows={3}
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              placeholder="e.g. In timings do not match gate pass / invalid reason"
            />
          </Modal>

        </Content>
      </Layout>
    </Layout>
  );
};

export default AttendanceManagement;
